<template>
  <LNavMobile :is-main="isMain" :is-contacts="isContacts" :is-gallery="isGallery" :is-about="isAbout"
    :is-restaurant="isRestaurant" :is-rooms="isRooms" />
  <section class="top-index hidden xl:flex">
    <section class="left-nav">
      <ul>
        <li class="nav-item">
          <nuxt-link :class="{ active: isContacts }" :to="localePath({ path: '/contacts' })">{{ t("header.contacts")
            }}</nuxt-link>
        </li>
        <li class="nav-item">
          <nuxt-link :class="{ active: isGallery }" :to="localePath({ path: '/gallery' })">{{ t("header.gallery")
            }}</nuxt-link>
        </li>
        <li class="nav-item">
          <nuxt-link :class="{ active: isAbout }" :to="localePath({ path: '/about' })">{{ t("header.about") }}</nuxt-link>
        </li>
        <li class="nav-item">
          <nuxt-link :class="{ active: isRestaurant }" :to="localePath({ path: '/restaurant' })">{{ t("header.restaurant")
            }}</nuxt-link>
        </li>
        <li class="nav-item">
          <nuxt-link :class="{ active: isRooms }" :to="localePath({ path: '/rooms' })">{{ t("header.rooms") }}</nuxt-link>
        </li>
      </ul>
    </section>
    <section class="central-logo">
      <nuxt-link :to="localePath({ path: '/' })">
        <img src="/img/logo_full.svg" alt="Leon Hotel Logo" />
      </nuxt-link>
    </section>
    <section class="right-nav">
      <ul>
        <li class="nav-item call-back" @click="setCallBack(true)">
          {{ t("header.call_back") }}
        </li>
        <li class="nav-item">
          <a href="tel:7-940-962-1100">+7 (940) 962-11-00</a>
        </li>
        <li class="nav-item">
          <ul>
            <li class="social">
              <a href="https://www.facebook.com/LeonHotel.abkhazia">
                <img src="/img/facebook.svg" alt="Facebook" />
              </a>
            </li>
            <li class="social">
              <a href="https://api.whatsapp.com/send?phone=79409621100">
                <img src="/img/whatsapp.svg" alt="WhatsApp" />
              </a>
            </li>
            <li class="social">
              <a href="https://www.instagram.com/hotel_restaurant_leon">
                <img src="/img/instagram.svg" alt="Instagram" />
              </a>
            </li>
          </ul>
        </li>
        <li class="nav-item lang">
          <ul class="flex flex-row gap-2 justify-start items-center">
            <li>
              <nuxt-link class="text-white font-lato text-lg cursor-pointer"
                :class="{ 'text-[#d9ccaf]': $i18n.locale === 'ru' }" :to="switchLocalePath('ru')">
                {{ t("links.ru") }}
              </nuxt-link>
            </li>
            <li class="mx-2 text-white font-lato text-lg">/</li>
            <li>
              <nuxt-link class="text-white font-lato text-lg cursor-pointer"
                :class="{ 'text-[#d9ccaf]': $i18n.locale === 'en' }" :to="switchLocalePath('en')">
                {{ t("links.en") }}
              </nuxt-link>
            </li>
          </ul>
        </li>
      </ul>
    </section>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

const { t } = useI18n()
const store = useMainStore()
const route = useRoute()
const switchLocalePath = useSwitchLocalePath()
const localePath = useLocalePath()

// State
const mainPlash = ref<string>('/img/logo_small.svg')
const isMain = ref(false)
const isContacts = ref(false)
const isGallery = ref(false)
const isAbout = ref(false)
const isRestaurant = ref(false)
const isRooms = ref(false)

// Computed
const isOpen = computed(() => store.menu)

// Methods
const setCallBack = (value: boolean) => store.setCallBack(value)
const setIsOpen = (value: boolean) => store.setMenu(value)

const updatePlash = () => {
  const path = route.path

  // Reset all states
  const resetStates = () => {
    isMain.value = false
    isContacts.value = false
    isGallery.value = false
    isAbout.value = false
    isRestaurant.value = false
    isRooms.value = false
  }

  // Set states based on path
  switch (path) {
    case '/':
    case '/en/':
      mainPlash.value = '/img/logo_full.svg'
      resetStates()
      isMain.value = true
      break

    case '/contacts':
    case '/en/contacts':
      mainPlash.value = '/img/logo_small.svg'
      resetStates()
      isContacts.value = true
      break

    case '/gallery':
    case '/en/gallery':
      mainPlash.value = '/img/logo_small.svg'
      resetStates()
      isGallery.value = true
      break

    case '/about':
    case '/en/about':
      mainPlash.value = '/img/logo_small.svg'
      resetStates()
      isAbout.value = true
      break

    case '/restaurant':
    case '/en/restaurant':
      mainPlash.value = '/img/logo_small.svg'
      resetStates()
      isRestaurant.value = true
      break

    case '/rooms':
    case '/en/rooms':
      mainPlash.value = '/img/logo_small.svg'
      resetStates()
      isRooms.value = true
      break

    default:
      mainPlash.value = '/img/logo_small.svg'
      resetStates()
  }
}

// Watch for route changes
watch(() => route.path, () => {
  updatePlash()
})

const navMenu = () => {
  setIsOpen(!isOpen.value)
}

// Lifecycle
onMounted(() => {
  updatePlash()
})
</script>

<style scoped lang="scss">
.top-index {
  position: absolute;
  z-index: 15;
  align-items: flex-start;
  align-content: space-between;
}

.logoImage {
  position: absolute;
  left: 20px;
  top: 9px;
  height: 48px;
  width: 83px;
  background-color: white;
}

.logoImage img {
  height: 100%;
  filter: invert(1);
}


.navbar-close-icon {
  position: absolute;
  right: 10px;
  top: 5px;
  // font-size: 50px;
  // color: white;
  background-image: url("/img/close-popup.svg");
  background-size: cover;
  cursor: pointer;
  height: 55px;
  width: 55px;
}

ul {
  list-style-type: none;
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  margin: 0;
  padding: 0;
}

.left-nav {
  padding-left: 50px;
  padding-right: 65px;
  padding-top: 20px;
}

.right-nav {
  padding-left: 65px;
  padding-top: 20px;
  padding-right: 50px;
}

.left-nav ul li a {
  color: white;
  text-decoration: none;
  font-family: "Lato";
  font-size: 20px;
  text-decoration: none;
}

.left-nav ul li a:hover,
.left-nav ul li a:focus,
.left-nav ul li a.active {
  border-bottom: 2px solid #d9ccaf;
}

.left-nav,
.right-nav {
  width: calc((100vw - 222px) / 2);
}

.central-logo {
  width: 222px;
  text-align: center;
}

.central-logo img {
  width: 158px;
  margin-left: auto;
  margin-right: auto;
}

.social a img {
  height: 29px;
  width: 29px;
  margin-right: 20px;
  filter: invert(1);
  margin-top: -4px;
}

.social:last-child a img {
  margin: 0;
  margin-top: -4px;
}

.lang ul li:first-child {
  margin-left: 12px;
  padding-right: 7.5px;
  color: white;
  font-family: "Lato";
  font-size: 20px;
}

.lang ul li:last-child {
  padding-left: 7.5px;
  color: white;
  font-family: "Lato";
  font-size: 20px;
}

.nav-item a {
  color: white;
  font-family: "Lato";
  font-size: 20px;
  line-height: 1;
}

li.nav-item.call-back {
  color: white;
  font-family: "Lato";
  font-size: 20px;
  cursor: pointer;
}

.central-logo img[src="/img/logo_full.svg"] {
  width: 222px;
}

@media (max-width: var(--breakpoint-xl)) and (min-width: var(--breakpoint-lg)) {

  .left-nav ul li a,
  .right-nav ul li a,
  li.nav-item.call-back,
  .nav-item.lang ul li a {
    font-size: 16px !important;
  }

  .nav-item.lang ul li {
    margin-top: -3px;
  }

  .social a img {
    height: 20px !important;
    width: 20px !important;
    margin-right: 10px;
    margin-top: 0px !important;
  }

  .social:last-child a img {
    margin-right: 0;
  }

  .left-nav,
  .right-nav {
    width: calc((100vw - 158px) / 2);
    padding-left: 15px;
    padding-right: 15px;
  }

  .central-logo img[src="/img/logo_full.svg"] {
    width: 158px;
  }
}

@media (max-width: var(--breakpoint-lg)) {
  .request_call {
    text-align: left;
    text-decoration: underline;
    font-size: 19px;
    margin: 22px 0 0 22px;
    color: #ffffff;
    font-family: "Lato";
  }

  .navbar-mobile-side {
    display: block;
  }

  .top-index {
    flex-direction: column;
  }

  .navbar-mobile-side {
    position: relative;
    left: 0;
    top: 0;
    bottom: 100vh;
    width: 100vw;
    right: 0;
  }

  .navbar-mobile-side.opened {
    width: 100vw;
    z-index: 15;
    background-color: white;
    bottom: 0 !important;
    height: 100vh;
    display: block;
    position: absolute;

  }

  .navbar-mobile-side .top {
    display: flex;
    height: 66px;
    width: 100%;
    border-bottom: 1px solid black;
    position: relative;
    float: left;
  }

  .top .logo {
    height: 100%;
    width: 105px;
    display: flex;
    margin-left: 0;
    margin-right: auto;
  }

  .top .logo a {
    margin: auto;
  }

  .navbar-mobile-side .top .logo img {
    height: 49px;
    margin: auto;
    filter: invert(1) !important;
  }

  .navbar-mobile-side .menu {
    display: flex;
    height: calc(100vh - 67px);
    width: 100vw;
    float: left;
    padding-top: 127px;
    padding-bottom: 127px;
  }

  .navbar-mobile-side .bottom {
    position: absolute;
    width: 100vw;
    left: 74px;
    display: flex;
  }

  .bottom {
    bottom: 70px;
  }

  .navbar-mobile-side .bottom ul {
    // width: 50%;
    display: flex;
    justify-content: space-between;
  }

  .bottom a {
    color: black;
  }

  .bottom img {
    filter: invert(0) !important;
    height: 29px !important;
    width: 29px !important;
    margin-right: 20px !important;
  }

  .nav-item.lang ul li a {
    font-size: 20px !important;
  }

  .nav-item.lang ul li {
    padding-top: 3px !important;
  }

  .menu ul {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-left: 70px;
  }

  .menu ul li a {
    color: #000;
    font-size: 23px;
  }

  .menu ul li {
    margin-bottom: 23.5px;
  }

  .menu ul li:last-child {
    margin-bottom: 0;
  }

  .left-nav,
  .right-nav {
    display: none;
  }

  .central-logo {
    margin-top: 91px;
    margin-left: auto;
    margin-right: auto;
  }

  .navbar-light .navbar-toggler {
    color: rgba(0, 0, 0, 0.5);
    border-color: rgba(0, 0, 0, 0.1);
  }

  button:not(:disabled) {
    cursor: pointer;
  }

  .navbar-toggler {
    padding: 0;
    font-size: 1.25rem;
    line-height: 1;
    background-color: transparent;
    border: 1px solid transparent;
    width: 36px;
    height: 24px;
    margin-top: auto;
    margin-bottom: auto;
    margin-left: auto;
    margin-right: 20px;
  }

  .navbar-toggler-icon {
    display: inline-block;
    width: 100%;
    height: 100%;
    vertical-align: middle;
    content: "";
    background: no-repeat center center;
    background-size: 100% 100%;
  }

  .navbar-toggler-icon {
    background-image: url(/img/icons/nav.svg);
  }

  button:focus,
  .navbar-toggler-icon:focus {
    outline: 0px !important;
  }

  img.white {
    filter: invert(1) !important;
  }
}
</style>
